import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components/macro";
import { Trans } from "react-i18next";

const BaseWrap = styled.div<{ $toggled: boolean }>`
  display: flex;
  align-items: center;
  background-color: white;
  color: ${({ $toggled, theme }) => ($toggled ? theme.duo.palette.blueMain : theme.duo.color.text)};
  border: 1px solid ${({ $toggled, theme }) => ($toggled ? theme.duo.palette.blueMain : theme.duo.color.grey)};
`;

const RoundWrap = css`
  padding: 10px 15px 10px 26px;
  border-radius: 50px;
  line-height: 1.35;
  min-height: 60px;
  gap: 10px;
  justify-content: space-between;
`;

const SquareWrap = css`
  padding: 10px;
  border-radius: 10px;
  font-size: 14px;
  gap: 9px;
  flex-direction: column;
  justify-content: center;
`;

const ToggleItemWrap = styled(BaseWrap)<{ $itemStyleType: string }>`
  ${({ $itemStyleType }) => ($itemStyleType === "square" ? SquareWrap : RoundWrap)}
  position: relative;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextWrap = styled.div`
  flex: 1;
  &::first-letter {
    text-transform: uppercase;
  }
`;

const ExtraContentWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export type ItemData = {
  id: string;
  icon?: React.ReactNode;
  text?: React.ReactNode;
  extraContent?: React.ReactNode;
  translate?: boolean;
};

type Props = ItemData & {
  toggle?: boolean;
  onToggle?: (toggled: boolean) => void;
  itemStyleType?: "rounded" | "square";
};

const ToggleItem: React.FC<Props> = ({
  icon,
  text,
  extraContent,
  toggle,
  onToggle,
  itemStyleType = "rounded",
  translate = true
}) => {
  const [toggled, setToggled] = useState(toggle || false);

  useEffect(() => {
    if (toggle !== undefined) {
      setToggled(toggle);
    }
  }, [toggle]);

  const handleToggle = () => {
    const newToggledState = !toggled;
    setToggled(newToggledState);
    onToggle?.(newToggledState);
  };

  return (
    <>
      <ToggleItemWrap
        className={toggled ? "toggled" : ""}
        onClick={handleToggle}
        $toggled={toggled}
        $itemStyleType={itemStyleType}
      >
        {icon && <IconWrap>{icon}</IconWrap>}
        {text && <TextWrap>{translate ? <Trans>{text}</Trans> : text}</TextWrap>}
        {extraContent && <ExtraContentWrap>{extraContent}</ExtraContentWrap>}
      </ToggleItemWrap>
    </>
  );
};

export default ToggleItem;
